import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Select } from '/imports/ui/Select';
import { QueryPreview } from '/imports/ui/QueryPreview';
import { useMethod } from '/imports/hooks';
import { listCardLists, CardList } from '/imports/api/cardLists';

/**
 * Dropdown of Dynamic Card Lists, with a live match-count preview of the
 * selected list and a jump-off to create a new one.
 *
 * The "New list" escape hatch is what keeps pure references ergonomic: a
 * one-off strategy is still one extra click, not a reason to reintroduce
 * inline queries (and with them, per-fleet duplication).
 */
export const CardListPicker = ({ value, onChange, inheritLabel }: {
  value: string | null | undefined;
  onChange(listId: string | null): void;
  /** What "blank" means at this level, e.g. 'Inherit from Settings'. */
  inheritLabel: string;
}) => {
  const [res] = useMethod(listCardLists, { limit: 500, sort: { name: 1 } }, []);
  const lists = (res?.documents ?? []) as CardList[];

  const options = useMemo(() => ([
    { value: '', label: inheritLabel },
    ...lists.map((l) => ({ value: l._id, label: l.name })),
  ]), [lists, inheritLabel]);

  const selected = lists.find((l) => l._id === value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Select className="filter" value={value ?? ''} options={options} onChange={(v) => onChange((v as string) || null)} />
        {selected && (
          <Link className="text-caption text-primary hover:underline" to={`/card-lists/${selected._id}`}>
            edit list
          </Link>
        )}
        <Link className="text-caption text-fg-3 hover:text-fg-1 hover:underline" to="/card-lists/new">
          + new list
        </Link>
      </div>
      <QueryPreview query={selected?.query} emptyHint={inheritLabel} />
    </div>
  );
};
